import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../api/client';
import { ApiError } from '../api/types';
import type { Invite, InviteKind } from '../api/types';
import { theme } from '../theme';
import { fmtDate } from '../utils/format';
import { INVITE_STATE_LABEL, inviteLink, inviteState } from '../utils/invite';
import type { InviteState } from '../utils/invite';
import { Icon } from './Icon';
import { useToast } from './Toast';

/**
 * Секция «Приглашения» в настройках.
 *
 * Главное ограничение, из которого вырос весь UX: сервер хранит только хэш
 * токена и отдаёт сам токен ровно один раз — при создании. Значит «скопировать
 * ссылку» физически возможно только для инвайтов, созданных в этой сессии; для
 * списка выданных мы знаем лишь префикс и состояние. Поэтому свежие ссылки
 * живут отдельным блоком наверху и не исчезают сами: их убирает пользователь,
 * когда скопировал.
 */

const KIND_LABEL: Record<InviteKind, string> = {
  new_household: 'новая семья',
  join: 'в мою семью',
};

const KIND_HINT: Record<InviteKind, string> = {
  new_household: 'Новая семья — гость заведёт свой бюджет с нуля, ваших записей он не увидит.',
  join: 'В мою семью — гость попадёт в ваш бюджет и будет видеть все записи.',
};

/** Ссылка, показанная один раз: живёт только в памяти вкладки. */
interface FreshInvite {
  key: number;
  kind: InviteKind;
  link: string;
  expires_at: string;
}

/**
 * Копирование в буфер. Живёт здесь, а не в utils: единственные потребители —
 * эта панель и соседняя секция токена импорта в Settings.
 *
 * Возвращает false вместо исключения — в небезопасном контексте (http://, старый
 * WebView) `navigator.clipboard` отсутствует, и это не ошибка, а повод сказать
 * «выделите вручную»: текст ссылки на экране и никуда не денется.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function InvitePanel() {
  const toast = useToast();
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [failMsg, setFailMsg] = useState<string | null>(null);
  const [items, setItems] = useState<Invite[]>([]);
  const [fresh, setFresh] = useState<FreshInvite[]>([]);
  const [busy, setBusy] = useState(false);
  const nowIso = new Date().toISOString();
  // Ключ для списка свежих ссылок: Date.now() склеил бы два создания подряд.
  const freshKeyRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { items: list } = await api.listInvites();
        if (cancelled) return;
        setItems(list);
        setStatus('ready');
      } catch (e) {
        if (cancelled) return;
        setFailMsg(e instanceof ApiError ? e.message : 'Не удалось загрузить приглашения');
        setStatus('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function reload() {
    try {
      const { items: list } = await api.listInvites();
      setItems(list);
    } catch {
      // Список — вторичен: ссылка уже выдана и показана выше. Молча оставляем
      // прежний список, следующее открытие настроек перечитает.
    }
  }

  async function handleCreate(kind: InviteKind) {
    setBusy(true);
    try {
      const created = await api.createInvite({ kind });
      setFresh((prev) => [
        {
          key: ++freshKeyRef.current,
          kind,
          link: inviteLink(created.token, window.location.origin),
          expires_at: created.expires_at,
        },
        ...prev,
      ]);
      await reload();
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Не удалось создать ссылку', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke(invite: Invite) {
    if (!confirm(`Отозвать приглашение ${invite.id_prefix}? Ссылка перестанет работать.`)) return;
    setBusy(true);
    try {
      await api.revokeInvite(invite.id_prefix);
      setItems((prev) => prev.filter((i) => i.id_prefix !== invite.id_prefix));
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Не удалось отозвать', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy(link: string) {
    if (await copyToClipboard(link)) toast.show('Ссылка скопирована');
    else toast.show('Буфер недоступен — выделите ссылку вручную', 'error');
  }

  return (
    <Section title="Приглашения">
      {status === 'loading' && (
        <div style={{ fontSize: 13, color: theme.colors.sub }}>Загрузка…</div>
      )}

      {status === 'failed' && (
        <div style={{ fontSize: 13, color: theme.colors.sub, lineHeight: 1.6 }}>
          Приглашения сейчас недоступны{failMsg ? ` — ${failMsg}` : ''}.
        </div>
      )}

      {status === 'ready' && (
        <>
          <div style={{ fontSize: 12, color: theme.colors.sub, lineHeight: 1.6 }}>
            Ссылка одноразовая и живёт неделю. Кто перейдёт по ней — создаст пароль и
            попадёт туда, куда вы решили сейчас.
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              disabled={busy}
              onClick={() => void handleCreate('new_household')}
              style={busy ? { ...chipStyle, color: theme.colors.sub, cursor: 'default' } : chipStyle}
            >
              <Icon name="plus" size={14} />
              Новая семья
            </button>
            <button
              disabled={busy}
              onClick={() => void handleCreate('join')}
              style={busy ? { ...chipStyle, color: theme.colors.sub, cursor: 'default' } : chipStyle}
            >
              <Icon name="plus" size={14} />
              В мою семью
            </button>
          </div>

          <div style={{ fontSize: 11, color: theme.colors.sub, lineHeight: 1.6 }}>
            {KIND_HINT.new_household}
            <br />
            {KIND_HINT.join}
          </div>

          {fresh.map((f) => (
            <FreshLink
              key={f.key}
              fresh={f}
              onCopy={() => void handleCopy(f.link)}
              onHide={() => setFresh((prev) => prev.filter((x) => x.key !== f.key))}
            />
          ))}

          {items.length === 0 && (
            <div style={{ fontSize: 12, color: theme.colors.sub }}>
              Пока никого не приглашали.
            </div>
          )}

          {items.map((invite) => (
            <InviteRow
              key={invite.id_prefix}
              invite={invite}
              state={inviteState(invite, nowIso)}
              disabled={busy}
              onRevoke={() => void handleRevoke(invite)}
            />
          ))}
        </>
      )}
    </Section>
  );
}

function FreshLink({
  fresh,
  onCopy,
  onHide,
}: {
  fresh: FreshInvite;
  onCopy: () => void;
  onHide: () => void;
}) {
  return (
    <div
      style={{
        border: `1px solid ${theme.colors.ink}`,
        borderRadius: theme.radius.sm,
        padding: '12px 14px',
        background: theme.colors.bg,
        display: 'grid',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="feather" size={15} />
        <div style={{ fontSize: 13.5 }}>Ссылка готова — {KIND_LABEL[fresh.kind]}</div>
      </div>
      <div style={{ fontSize: 11, color: theme.colors.sub, lineHeight: 1.6 }}>
        Показываем один раз: на сервере остался только отпечаток. Закроете настройки —
        ссылку будет неоткуда взять, придётся выпускать новую.
      </div>
      <div
        style={{
          fontSize: 12,
          wordBreak: 'break-all',
          padding: '8px 10px',
          background: theme.colors.card,
          border: `1px solid ${theme.colors.line}`,
          borderRadius: theme.radius.sm,
          userSelect: 'all',
        }}
      >
        {fresh.link}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={onCopy} style={primaryBtnStyle}>
          Скопировать
        </button>
        <button onClick={onHide} style={ghostBtnStyle}>
          Скопировал, скрыть
        </button>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: theme.colors.sub }}>
          до {fmtDate(fresh.expires_at)}
        </div>
      </div>
    </div>
  );
}

function InviteRow({
  invite,
  state,
  disabled,
  onRevoke,
}: {
  invite: Invite;
  state: InviteState;
  disabled: boolean;
  onRevoke: () => void;
}) {
  const dim = state !== 'active';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 14, color: dim ? theme.colors.sub : theme.colors.ink }}>
          {KIND_LABEL[invite.kind]}
          {invite.email ? ` · ${invite.email}` : ''}
        </div>
        <div style={{ fontSize: 11, color: theme.colors.sub, marginTop: 2 }}>
          {invite.id_prefix} · выдана {fmtDate(invite.created_at)} · до {fmtDate(invite.expires_at)}
        </div>
      </div>
      <div
        style={{
          fontSize: 11,
          padding: '3px 8px',
          borderRadius: theme.radius.sm,
          border: `1px solid ${state === 'active' ? theme.colors.ink : theme.colors.line}`,
          color: state === 'active' ? theme.colors.ink : theme.colors.sub,
          whiteSpace: 'nowrap',
        }}
      >
        {INVITE_STATE_LABEL[state]}
      </div>
      <button
        disabled={disabled}
        onClick={onRevoke}
        title={state === 'active' ? 'Отозвать' : 'Убрать из списка'}
        aria-label={state === 'active' ? 'Отозвать приглашение' : 'Убрать приглашение из списка'}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 4,
          color: theme.colors.sub,
          cursor: disabled ? 'default' : 'pointer',
          display: 'flex',
        }}
      >
        <Icon name="trash" size={15} />
      </button>
    </div>
  );
}

/**
 * Та же обёртка секции, что в Settings.tsx. Дублируется намеренно: панель —
 * самостоятельный блок, а импорт из экрана в компонент дал бы цикл
 * Settings → InvitePanel → Settings.
 */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize: 11,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          color: theme.colors.sub,
          marginBottom: 8,
          paddingLeft: 4,
        }}
      >
        {title}
      </div>
      <div
        style={{
          background: theme.colors.card,
          border: `1px solid ${theme.colors.line}`,
          borderRadius: theme.radius.lg,
          padding: 16,
          display: 'grid',
          gap: 14,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Интерактивный чип — радиус 999 по закону «Б» (FRONTEND_CORE §4). */
const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 14px',
  fontSize: 13,
  fontFamily: theme.font,
  color: theme.colors.ink,
  background: theme.colors.bg,
  border: `1px solid ${theme.colors.line}`,
  borderRadius: 999,
  cursor: 'pointer',
};

const primaryBtnStyle: CSSProperties = {
  padding: '8px 14px',
  fontSize: 13,
  fontFamily: theme.font,
  background: theme.colors.ink,
  color: theme.colors.paper,
  border: 'none',
  borderRadius: theme.radius.sm,
  cursor: 'pointer',
};

const ghostBtnStyle: CSSProperties = {
  padding: '8px 14px',
  fontSize: 13,
  fontFamily: theme.font,
  background: 'transparent',
  color: theme.colors.sub,
  border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.sm,
  cursor: 'pointer',
};
